import { clone, isFinite, max, min, round, sumBy, without } from 'lodash-es';
import { CutoutData, QuoteData } from '../quote-data';

export class GridHelpers {
  static calculateArea(qd: QuoteData): number {
    let area = 0;
    area +=
      qd.FloorData.GridData.XDim * qd.FloorData.GridData.YDim * qd.FloorData.TiersData.TierCount;
    for (const tier of qd.FloorData.TiersData.TierData) {
      let intersectedArea = 0;
      const intersectingCutouts = new Array<CutoutData>();
      if (tier.CutoutData.length > 1) {
        let otherCutouts = clone(tier.CutoutData);
        for (const cutout of tier.CutoutData) {
          for (const otherCutout of otherCutouts) {
            if (cutout === otherCutout) {
              continue;
            }
            const intersectingCutout = this.getIntersectingCutout(cutout, otherCutout);
            intersectingCutouts.push(intersectingCutout);
            intersectedArea += intersectingCutout.XDim * intersectingCutout.YDim;
          }
          otherCutouts = without(otherCutouts, cutout);
        }

        if (intersectingCutouts.length > 1) {
          let otherIntersectingCutouts = clone(intersectingCutouts);
          for (const intersectingCutout of intersectingCutouts) {
            for (const otherIntersectingCutout of otherIntersectingCutouts) {
              if (intersectingCutout === otherIntersectingCutout) {
                continue;
              }
              const nestedIntersection = this.getIntersectingCutout(
                intersectingCutout,
                otherIntersectingCutout,
              );
              intersectedArea -= nestedIntersection.XDim * nestedIntersection.YDim;
            }
            otherIntersectingCutouts = without(otherIntersectingCutouts, intersectingCutout);
          }
        }
      }
      area = area - sumBy(tier.CutoutData, (c: any) => c.XDim * c.YDim) + intersectedArea;
    }
    return area;
  }

  private static getIntersectingCutout(c1: CutoutData, c2: CutoutData): CutoutData {
    if (
      c2.X < c1.X + c1.XDim &&
      c1.X < c2.X + c2.XDim &&
      c2.Y < c1.Y + c1.YDim &&
      c1.Y < c2.Y + c2.YDim
    ) {
      const top = max([c1.Y, c2.Y]);
      const left = max([c1.X, c2.X]);
      const right = min([c1.X + c1.XDim, c2.X + c2.XDim]);
      const bottom = min([c1.Y + c1.YDim, c2.Y + c2.YDim]);

      const width = right > left ? right - left : left - right;
      const height = bottom > top ? bottom - top : top - bottom;

      return { X: left, Y: top, XDim: width, YDim: height } as CutoutData;
    }
    return { X: 0, Y: 0, XDim: 0, YDim: 0 } as CutoutData;
  }

  private static getIntersectingLength(c1: CutoutData, c2: CutoutData): number | null {
    if (c2.X === c1.X + c1.XDim) {
      // X axis aligns, intersects along Y
      return min([c1.Y + c1.YDim, c2.Y + c2.YDim]) - max([c1.Y, c2.Y]);
    } else if (c2.Y === c1.Y + c1.YDim) {
      // Y axis aligns, intersects along X
      return min([c1.X + c1.XDim, c2.X + c2.XDim]) - max([c1.X, c2.X]);
    }
    return null;
  }

  static calculateRoundedArea(qd: QuoteData, decimalPlaces: number = 3): number {
    return round(GridHelpers.calculateArea(qd), decimalPlaces);
  }

  static calculateRoundedPerimeter(qd: QuoteData): number {
    return round(GridHelpers.calculatePerimeter(qd), 3);
  }

  static calculateTotalFloorHeight(qd: QuoteData): number {
    const sum = qd.FloorData.TiersData.TierData.reduce((s, tier) => (s += tier.Height), 0);
    return sum;
  }

  /**
   * Calculates the perimeter of the Floor,
   * including cutouts
   */
  static calculatePerimeter(qd: QuoteData): number {
    // start with floor perimeter
    let perimeterLength =
      qd.FloorData.TiersData.TierCount *
      (2 * (qd.FloorData.GridData.XDim + qd.FloorData.GridData.YDim));

    for (const tier of qd.FloorData.TiersData.TierData) {
      for (const cutout of tier.CutoutData) {
        // add perimeter of cutout
        perimeterLength += 2 * (cutout.XDim + cutout.YDim);
        // check and remove intersections with the edge of the floor
        if (cutout.X === 0) {
          perimeterLength -= 2 * cutout.YDim;
        }
        if (cutout.X + cutout.XDim >= qd.FloorData.GridData.XDim) {
          perimeterLength -= 2 * cutout.YDim;
        }
        if (cutout.Y === 0) {
          perimeterLength -= 2 * cutout.XDim;
        }
        if (cutout.Y + cutout.YDim >= qd.FloorData.GridData.YDim) {
          perimeterLength -= 2 * cutout.XDim;
        }
        if (tier.CutoutData.length > 1) {
          // check and remove intersections with other cutouts
          let otherCutouts = clone(tier.CutoutData);
          for (const otherCutout of otherCutouts) {
            if (cutout === otherCutout) {
              continue;
            }
            const intersectingLength = this.getIntersectingLength(cutout, otherCutout);
            if (intersectingLength !== null && isFinite(intersectingLength)) {
              perimeterLength -= 2 * intersectingLength;
            }
          }
          otherCutouts = without(otherCutouts, cutout);
        }
      }
    }
    return perimeterLength;
  }
}
